import React, { useRef, useEffect } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { Image } from 'expo-image';
import ExploreCardOverlay from './ExploreCardOverlay';
import { exploreMediaZoomStyles as mz } from './exploreCardMediaZoomStyles';

const isWeb = Platform.OS === 'web';
const isMacOS = Platform.OS === 'macos';
const isMobileNative = Platform.OS === 'ios' || Platform.OS === 'android';

function WebVideo({ uri, style }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.play().catch(() => {});
  }, [uri]);

  return (
    <video
      ref={ref}
      src={uri}
      autoPlay
      loop
      muted
      playsInline
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
        ...style,
      }}
    />
  );
}

// Skip the auto-play preview entirely in the feed on iOS and Android: every
// card was instantiating its own expo-video player, which spawns a native
// MediaCodec / AVPlayer pipeline. Phones can only juggle a handful of those
// before performance tanks (lag, ANRs on Android, dropped frames on iOS) and
// eventually decoder allocation fails outright. The thumbnail still shows
// the first frame; users tap the card to open the lightbox for the full
// video. macOS keeps the existing native player.
let NativeVideo;
if (!isWeb && !isMobileNative) {
  const { useVideoPlayer, VideoView } = require('expo-video');
  NativeVideo = React.memo(({ uri, style }) => {
    const player = useVideoPlayer(uri, (p) => {
      p.loop = true;
      p.muted = true;
      p.play();
    });
    return (
      <VideoView
        player={player}
        style={[styles.video, style]}
        pointerEvents="none"
        nativeControls={false}
        allowsFullscreen={false}
        allowsPictureInPicture={false}
        contentFit="cover"
      />
    );
  });
}

const ExploreCardVideo = React.memo(({ item, style, hovered }) => {
  return (
    <>
      <View style={mz.clip}>
        <View style={[mz.zoom, hovered && mz.zoomHovered]}>
          <Image
            source={{ uri: item.thumbnailPath || item.cdnPath }}
            style={[styles.thumbnail, style]}
            pointerEvents="none"
            contentFit="cover"
            placeholder={item.blurhash ? { blurhash: item.blurhash } : undefined}
            recyclingKey={`${item.id}-bg`}
            priority="low"
          />
          {isWeb
            ? hovered && <WebVideo uri={item.cdnPath} />
            : NativeVideo && <NativeVideo uri={item.cdnPath} style={style} />}
        </View>
      </View>
      <ExploreCardOverlay
        prompt={item.displayName || item.prompt}
        modelName={item.modelName}
        modelSlug={item.modelSlug}
        mediaType="video"
        duration={item.metadata?.duration}
        isPlusShowcase={item.flags?.isPlusShowcase}
        hovered={hovered}
        variationCount={item.variationCount}
      />
    </>
  );
});

const styles = StyleSheet.create({
  thumbnail: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
  },
  video: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
  },
});

export default ExploreCardVideo;
